import routes, { renderRoutes } from '@/routes'
import ErrorBoundary from '@components/ErrorBoundary'
import GlobalStyles from '@components/GlobalStyles'
import ScrollReset from '@components/ScrollReset'
import { AuthProvider } from '@contexts/JWTAuthContext'
import MomentUtils from '@date-io/moment'
import useSettings from '@hooks/useSettings'
import {
  CssBaseline,
  Slide,
  StylesProvider,
  ThemeProvider,
  jssPreset
} from '@material-ui/core'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import api from '@micmnt/apis'
import { createDashboardTheme } from '@theme'
import { createBrowserHistory } from 'history'
import { create } from 'jss'
import rtl from 'jss-rtl'
import { SnackbarProvider } from 'notistack'
import { memo } from 'react'
import { Router } from 'react-router-dom'
import 'moment/locale/it'
import { ChartsProvider } from '@contexts/ChartsContext'
import apiConfig from './api/config'
import { configuration } from './config'

const jss = create({ plugins: [...jssPreset().plugins, rtl()] })
const history = createBrowserHistory()

// Inizializzazione del servizio di chiamate API
api.init({
  baseUrl: configuration.apiUrl,
  savedUrls: apiConfig,
  jwtTokenName: 'accessToken',
  errorInterceptor: (error) => {
    const errorStatus = error?.response?.data?.error?.statusCode || null
    const errorMessage = error?.response?.data?.error?.message || null
    const errorName = error?.response?.data?.error?.name || null
    const errorType = error?.response?.data?.error?.type || null
    
    const strMatch = (str, words) => str && words.some(w => str.toLowerCase().includes(w))
    
    const isTokenError = strMatch(errorMessage, ['token', 'jwt']) || strMatch(errorName, ['token', 'jwt']) || strMatch(errorType, ['token', 'jwt'])
    const isExpired = strMatch(errorMessage, ['expire', 'scadut']) || strMatch(errorName, ['expire', 'scadut']) || strMatch(errorType, ['expire', 'scadut'])
    
    if (errorStatus === 401 && isTokenError && isExpired) {
      // Per difenderci dai falsi positivi del backend, verifichiamo la reale scadenza del JWT
      let trulyExpired = false;
      try {
        const token = window.localStorage.getItem('accessToken');
        if (token && token.split('.').length === 3) {
          const base64Url = token.split('.')[1];
          let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          while (base64.length % 4) {
            base64 += '=';
          }
          const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
              return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          }).join(''));
          const payload = JSON.parse(jsonPayload);
          if (payload && payload.exp) {
            trulyExpired = (payload.exp * 1000) < Date.now();
          }
        }
      } catch (e) {
        console.warn("Impossibile decodificare il token per verificarne la scadenza", e);
      }

      if (trulyExpired) {
        window.localStorage.removeItem('accessToken')
        window.location.replace(`${window.location.origin}/login`)
      } else {
        console.warn("Il backend ha restituito un falso token expired. Logout ignorato.", { errorMessage });
      }
    }
  }
})

const App = () => {
  // Inizializzazione Smartlook
  React.useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return
    if (!window.smartlook) {
      ((d) =>{
        const o = (window.smartlook = function() { o.api.push(arguments); });
        const h = d.getElementsByTagName('head')[0];
        const c = d.createElement('script');
        o.api = [];
        c.async = true;
        c.type = 'text/javascript';
        c.src = 'https://web-sdk.smartlook.com/recorder.js';
        h.appendChild(c);
      })(document);
    }
    window.smartlook('init', '32afa7662b4d96449aa5e73f4a98c70239ef7f62', { region: 'eu' });
  }, []);
  const { settings } = useSettings()

  // creo il tema
  const theme = createDashboardTheme({
    direction: settings.direction,
    responsiveFontSizes: settings.responsiveFontSizes,
    theme: settings.theme
  })

  return (
    <ThemeProvider theme={theme}>
      <StylesProvider jss={jss}>
        <CssBaseline />
        <MuiPickersUtilsProvider utils={MomentUtils} locale='it'>
          <SnackbarProvider
            classes={{ root: 'snackbarProviderRoot' }}
            maxSnack={5}
            TransitionComponent={Slide}
            anchorOrigin={{
						  vertical: 'bottom',
						  horizontal: 'left'
            }}
            autoHideDuration={4000}
          >
            <ErrorBoundary>
              <Router history={history}>
                <ChartsProvider>
                  <AuthProvider>
                    <GlobalStyles />
                    <ScrollReset />
                    {renderRoutes(routes)}
                  </AuthProvider>
                </ChartsProvider>
              </Router>
            </ErrorBoundary>
          </SnackbarProvider>
        </MuiPickersUtilsProvider>
      </StylesProvider>
    </ThemeProvider>
  )
}

export default memo(App)
